import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import urlConstants from '../constants/urlConstants';
import logo from '../assets/svgs/insideout-logo-licht.svg';
import logoZonderSlogan from '../assets/svgs/insideout-logo-licht-zonder-slogan.svg';

const colors = {
  navyDeep: '#0F1C33',
  navy: '#182A48',
  navyLight: '#243B63',
  gold: '#C88622',
  goldSoft: '#D4AB32',
  goldText: '#9E611A',
  foil: 'linear-gradient(90deg, #9E611A 0%, #C88622 30%, #DAC23E 62%, #A5661B 100%)',
  cream: '#FFF7EE',
  inkSoft: '#4A5468',
  okerCard: '#C99A3E',
  okerInkSoft: '#2E3A52',
};

const serif = '"Cormorant Garamond", Georgia, "Times New Roman", serif';
const sans = '"Open Sans", "Helvetica Neue", Arial, sans-serif';

const foilText = css`
  background: ${colors.foil};
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  padding-right: .06em;
`;

const Splash = styled.main`
  position: relative;
  min-height: 100vh;
  isolation: isolate;
  box-sizing: border-box;
  background: radial-gradient(ellipse at 50% 32%, ${colors.navyLight} 0%, ${colors.navy} 55%, ${colors.navyDeep} 100%);
  color: ${colors.cream};
  font-family: ${sans};
  font-size: 16px;
  line-height: 1.6;
  padding: clamp(40px, 7vw, 88px) clamp(16px, 5vw, 64px);

  *, *::before, *::after {
    box-sizing: border-box;
  }
`;

// Achtergrondbeeld: echte vrouw met gezicht naar het licht (Pexels 5857310), als doorzichtig sjabloon
const bgVideoHd = 'https://videos.pexels.com/video-files/5857310/5857310-hd_1366_720_25fps.mp4';
const bgVideoSd = 'https://videos.pexels.com/video-files/5857310/5857310-sd_960_506_25fps.mp4';

const BgVideo = styled.video`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  object-fit: cover;
  z-index: -1;
  opacity: 0.35;
  mix-blend-mode: luminosity;
  pointer-events: none;
`;

const startBgVideo = (el) => {
  if (!el) return;
  el.muted = true;
  const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (!reduce) {
    const p = el.play();
    if (p && p.catch) p.catch(() => {});
  }
};

const Frame = styled.div`
  position: absolute;
  top: clamp(10px, 2vw, 22px);
  right: clamp(10px, 2vw, 22px);
  bottom: clamp(10px, 2vw, 22px);
  left: clamp(10px, 2vw, 22px);
  border: 1px solid rgba(200, 134, 34, 0.6);
  pointer-events: none;

  &::after {
    content: "";
    position: absolute;
    top: 7px;
    right: 7px;
    bottom: 7px;
    left: 7px;
    border: 1px solid rgba(200, 134, 34, 0.3);
  }
`;

const Inner = styled.div`
  position: relative;
  max-width: 1180px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: clamp(28px, 4vw, 44px);
  text-align: center;
`;

const Logo = styled.img`
  width: min(420px, 82vw);
  height: auto;
  display: block;
`;

// Engels logo: het logo zonder de ingetekende Nederlandse slogan, met de
// slogan als tekst eronder op dezelfde plek en in dezelfde stijl.
const LogoBlock = styled.div`
  width: min(420px, 82vw);
`;

const Tagline = styled.span`
  display: block;
  margin-top: min(18px, 3.53vw);
  padding-left: 0.32em;
  font-family: ${serif};
  font-weight: 600;
  font-size: min(21.4px, 4.19vw);
  line-height: 1;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  text-align: center;
  white-space: nowrap;
  ${foilText}
`;

const Intro = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;

  h1 {
    font-family: ${serif};
    font-weight: 500;
    font-size: 40px;
    line-height: 1.05;
    color: ${colors.cream};
    margin: 0;

    @media (min-width: 640px) {
      font-size: 64px;
    }

    em {
      font-style: italic;
      ${foilText}
    }
  }

  p {
    margin: 0;
    max-width: 46ch;
    color: rgba(245, 240, 232, 0.82);
    font-size: 17px;
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  color: ${colors.gold};

  &::before, &::after {
    content: "";
    width: 72px;
    height: 1px;
    background: ${colors.foil};
  }

  span {
    width: 6px;
    height: 6px;
    background: currentColor;
    transform: rotate(45deg);
  }
`;

const Choices = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;

  @media (min-width: 640px) {
    grid-template-columns: 1fr 1fr;
    gap: 24px;
  }

  @media (min-width: 992px) {
    grid-template-columns: 1fr 1fr 1fr;
  }
`;

const cardStyle = css`
  display: flex;
  flex-direction: column;
  gap: 14px;
  text-align: left;
  padding: clamp(24px, 3vw, 36px);
  text-decoration: none;
  font-size: 16px;
  font-weight: 400;
  border: 1px solid transparent;
  transition: transform .25s ease, border-color .25s ease, box-shadow .25s ease;

  &::before, &:hover::before {
    display: none;
  }

  &:hover, &:focus-visible {
    transform: translateY(-3px);
    border-color: ${colors.gold};
    box-shadow: 0 14px 34px rgba(0, 0, 0, 0.35);
    outline: none;
  }

  .age {
    font-family: ${serif};
    font-weight: 600;
    font-size: 14px;
    letter-spacing: 0.3em;
    text-transform: uppercase;
  }

  h2 {
    font-family: ${serif};
    font-weight: 500;
    font-size: 30px;
    line-height: 1.1;
    color: inherit;
    margin: 0;

    @media (min-width: 640px) {
      font-size: 38px;
    }
  }

  p {
    margin: 0;
    font-size: 15.5px;
  }

  .go {
    margin-top: auto;
    align-self: flex-start;
    font-family: ${sans};
    font-weight: 600;
    font-size: 13px;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    padding: 14px 22px;
    background: ${colors.navy};
    color: ${colors.cream};
  }
`;

const YoungCard = styled(Link)`
  ${cardStyle}
  background: ${colors.cream};

  &, &:visited { color: ${colors.navy}; }

  .age { color: ${colors.goldText}; }
  h2 em { ${foilText} }
  p { color: ${colors.inkSoft}; }
`;

const AdultCard = styled.a`
  ${cardStyle}
  background: ${colors.okerCard};

  &, &:visited { color: ${colors.navy}; }

  .age { color: ${colors.navy}; }
  h2 em { color: ${colors.navy}; }
  p { color: ${colors.okerInkSoft}; }
`;

const ParentCard = styled.a`
  ${cardStyle}
  background: ${colors.navyLight};

  &, &:visited { color: ${colors.cream}; }
  border-color: rgba(200, 134, 34, 0.45);

  .age { color: ${colors.goldSoft}; }
  h2 em { ${foilText} }
  p { color: rgba(255, 247, 238, 0.82); }
  .go {
    background: ${colors.foil};
    color: ${colors.navy};
  }
`;

const OlderLink = styled.a`
  display: inline-block;
  font-family: ${sans};
  font-size: clamp(16px, 1.8vw, 19px);
  font-weight: 600;
  line-height: 1.4;
  text-decoration: none;
  padding: 14px 28px;
  border: 1.5px solid ${colors.goldSoft};
  background: rgba(15, 28, 51, 0.45);
  transition: background .2s ease, color .2s ease;

  &, &:visited { color: ${colors.goldSoft}; }

  &::before, &:hover::before {
    display: none;
  }

  &:hover, &:focus-visible {
    background: ${colors.goldSoft};
    color: ${colors.navy};
  }
`;

const Foot = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  justify-content: center;
  padding-top: 22px;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: ${colors.foil};
    opacity: .8;
  }

  span {
    font-family: ${serif};
    font-weight: 600;
    font-size: clamp(16px, 1.7vw, 20px);
    letter-spacing: 0.3em;
    text-transform: uppercase;
    color: ${colors.goldSoft};
  }
`;

const LangToggle = styled(Link)`
  position: absolute;
  top: clamp(22px, 3.5vw, 40px);
  right: clamp(22px, 3.5vw, 40px);
  z-index: 2;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-family: ${sans};
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  letter-spacing: 0.14em;
  text-decoration: none;
  opacity: .85;

  &, &:visited { color: ${colors.cream}; }

  &::before, &:hover::before {
    display: none;
  }

  &:hover, &:focus-visible {
    opacity: 1;
    color: ${colors.goldSoft};
  }

  svg {
    width: 20px;
    height: 13px;
    display: block;
    border-radius: 2px;
    box-shadow: 0 0 0 1px rgba(244, 237, 228, 0.35);
  }
`;

const FlagNL = () => (
  <svg viewBox="0 0 9 6" aria-hidden="true">
    <rect width="9" height="6" fill="#21468B" />
    <rect width="9" height="4" fill="#fff" />
    <rect width="9" height="2" fill="#AE1C28" />
  </svg>
);

const FlagGB = () => (
  <svg viewBox="0 0 60 30" aria-hidden="true">
    <clipPath id="keuze-flag-s"><path d="M0,0v30h60V0z" /></clipPath>
    <clipPath id="keuze-flag-t"><path d="M30,15h30v15zv15H0zH0V0zV0h30z" /></clipPath>
    <g clipPath="url(#keuze-flag-s)">
      <path d="M0,0v30h60V0z" fill="#012169" />
      <path d="M0,0L60,30M60,0L0,30" stroke="#fff" strokeWidth="6" />
      <path d="M0,0L60,30M60,0L0,30" clipPath="url(#keuze-flag-t)" stroke="#C8102E" strokeWidth="4" />
      <path d="M30,0v30M0,15h60" stroke="#fff" strokeWidth="10" />
      <path d="M30,0v30M0,15h60" stroke="#C8102E" strokeWidth="6" />
    </g>
  </svg>
);

const copy = {
  nl: {
    title: 'InsideOut Nederland | Kies jouw programma',
    logoAlt: 'InsideOut, Haal eruit wat erin zit',
    headingBefore: 'Kies',
    headingEm: 'jouw',
    headingAfter: 'programma',
    intro: 'InsideOut helpt mensen tot hun recht te komen: als jongere, als student, als jonge professional of als opvoeder. Kies hieronder het programma dat past bij de fase waarin jij nu staat.',
    young: {
      label: 'Naar InsideOut voor jongeren van 12 tot 18 jaar',
      age: '12 tot 18 jaar',
      name: 'Jongeren',
      text: 'Leren omgaan met wat je voelt, keuzes maken die bij je passen en ontdekken wat er in je zit.',
      go: 'Naar jongeren',
    },
    adult: {
      href: urlConstants.JONGVOLWASSENEN_SITE,
      label: 'Naar InsideOut Jongvolwassenen voor 18 tot en met 30 jaar',
      age: '18 tot en met 30 jaar',
      name: 'Jongvolwassenen',
      text: 'Voor studenten en young professionals. Persoonlijk leiderschap met emotionele intelligentie als fundament.',
      go: 'Naar jongvolwassenen',
    },
    parent: {
      href: urlConstants.OPVOEDERS_SITE,
      label: 'Naar InsideOut voor opvoeders',
      age: 'Voor opvoeders',
      name: 'Opvoeders',
      text: 'Voor ouders, grootouders, docenten, coaches en iedereen die kinderen en jongeren begeleidt. Met hart voor opvoeden.',
      go: 'Naar opvoeders',
    },
    older: { href: urlConstants.OUDER_DAN_30, text: 'Ben je ouder dan 30? Ontdek de mogelijkheden en laat je gegevens achter' },
    foot: 'Zijn wie je bent',
    toggle: { to: urlConstants.HOME_EN, lang: 'en', label: 'English version', text: 'EN', Flag: FlagGB },
  },
  en: {
    title: 'InsideOut Nederland | Choose your programme',
    logoAlt: 'InsideOut, Unlock what’s inside',
    headingBefore: 'Choose',
    headingEm: 'your',
    headingAfter: 'programme',
    intro: 'InsideOut helps people fulfil their full potential: as a young person, a student, a young professional or someone raising children. Choose the programme that fits the stage you’re at right now.',
    young: {
      label: 'To InsideOut Youth, for ages 12 to 18 (in Dutch)',
      age: '12 to 18 years',
      name: 'Youth',
      text: 'Learning to handle what you feel, making choices that suit you and discovering what’s inside you. This programme is in Dutch.',
      go: 'To youth',
    },
    adult: {
      href: urlConstants.JONGVOLWASSENEN_SITE_EN,
      label: 'To InsideOut Young adults, for ages 18 to 30',
      age: '18 to 30 years',
      name: 'Young adults',
      text: 'For students and young professionals. Personal leadership, built on emotional intelligence.',
      go: 'To young adults',
    },
    parent: {
      href: urlConstants.OPVOEDERS_SITE_EN,
      label: 'To InsideOut for parents & caregivers',
      age: 'For parents & caregivers',
      name: 'Parents & caregivers',
      text: 'For parents and caregivers, grandparents, teachers, coaches and anyone who guides children and young people. Parenting from the heart.',
      go: 'To parents & caregivers',
    },
    older: { href: urlConstants.OUDER_DAN_30_EN, text: 'Over 30? Discover the possibilities and leave your details' },
    foot: 'Be who you are',
    toggle: { to: urlConstants.HOME, lang: 'nl', label: 'Nederlandse versie', text: 'NL', Flag: FlagNL },
  },
};

const KeuzePage = ({ lang = 'nl' }) => {
  const t = copy[lang] || copy.nl;
  const { Flag } = t.toggle;

  useEffect(() => {
    const previousTitle = document.title;
    const previousLang = document.documentElement.lang;
    document.title = t.title;
    document.documentElement.lang = lang;
    return () => {
      document.title = previousTitle;
      document.documentElement.lang = previousLang;
    };
  }, [lang, t.title]);

  return (
    <Splash>
      <BgVideo
        src={typeof window !== 'undefined' && window.innerWidth > 900 ? bgVideoHd : bgVideoSd}
        ref={startBgVideo}
        muted
        loop
        playsInline
        preload="auto"
        aria-hidden="true"
      />
      <Frame aria-hidden="true" />
      <LangToggle
        to={t.toggle.to}
        hrefLang={t.toggle.lang}
        lang={t.toggle.lang}
        aria-label={t.toggle.label}
      >
        <Flag />
        <span>{t.toggle.text}</span>
      </LangToggle>
      <Inner>
        {lang === 'en' ? (
          <LogoBlock>
            <Logo src={logoZonderSlogan} alt={t.logoAlt} />
            <Tagline aria-hidden="true">Unlock what’s inside</Tagline>
          </LogoBlock>
        ) : (
          <Logo src={logo} alt={t.logoAlt} />
        )}
        <Intro>
          <h1>
            {t.headingBefore}
            {' '}
            <em>{t.headingEm}</em>
            {' '}
            {t.headingAfter}
          </h1>
          <Divider aria-hidden="true"><span /></Divider>
          <p>{t.intro}</p>
        </Intro>
        <Choices>
          <YoungCard to={urlConstants.JONGEREN} aria-label={t.young.label}>
            <span className="age">{t.young.age}</span>
            <h2>
              InsideOut
              <br />
              <em>{t.young.name}</em>
            </h2>
            <p>{t.young.text}</p>
            <span className="go">{t.young.go}</span>
          </YoungCard>
          <AdultCard href={t.adult.href} aria-label={t.adult.label}>
            <span className="age">{t.adult.age}</span>
            <h2>
              InsideOut
              <br />
              <em>{t.adult.name}</em>
            </h2>
            <p>{t.adult.text}</p>
            <span className="go">{t.adult.go}</span>
          </AdultCard>
          <ParentCard href={t.parent.href} aria-label={t.parent.label}>
            <span className="age">{t.parent.age}</span>
            <h2>
              InsideOut
              <br />
              <em>{t.parent.name}</em>
            </h2>
            <p>{t.parent.text}</p>
            <span className="go">{t.parent.go}</span>
          </ParentCard>
        </Choices>
        <OlderLink href={t.older.href}>
          {t.older.text}
          {' '}
          &rarr;
        </OlderLink>
        <Foot><span>{t.foot}</span></Foot>
      </Inner>
    </Splash>
  );
};

export default KeuzePage;
